<template>
  <div />
</template>

<script>

/**
 * Vue: Boost api call (boost-api-call)
 * -----------------------------------------------------------------------------
 * Component to make Boost API calls.
 * https://help.boostcommerce.net/category/668-filter-and-search-apis-app-customization
 *
 * @param {String} buildFilterTree - Leave blank if filters aren't needed or
 * endpoint is 'search/suggest', otherwise value is '&build_filter_tree=true'.
 * @param {String} choices - Formatted filter choices to use in API call.
 * @param {Number} collectionId - Collection id to use for 'filter' endpoint.
 * @param {String} endpoint - Determines which API to use. Options are 'filter'
 * (for collections), 'search' or 'search/suggest' (predictive search).
 * @param {Number} limit - Number of products to retrieve per page.
 * @param {String} shop - Permanent shop domain.
 * @param {String} query - Search query.
 *
 */
import { mapActions } from 'vuex'

export default {
  name: 'BoostApiCall',

  props: {
    buildFilterTree: {
      type: Boolean,
      default: true,
    },
    choices: {
      type: String,
      default: '',
    },
    collectionId: {
      type: Number,
      default: null,
    },
    endpoint: {
      type: String,
      default: '',
    },
    limit: {
      type: Number,
      default: null,
    },
    shop: {
      type: String,
      default: '',
    },
    query: {
      type: String,
      default: '',
    },
  },

  emits: {
    'boost-updated': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      global: true,
      firstLoad: true,
      formattedQuery: '',
      saveEventType: '',
    }
  },

  watch: {

    /**
     * Watch choices update to get product results.
     */
    choices(choices) {
      this.getProductResults(choices)
    },

    /**
     * Watch query update to get product results.
     */
    query() {
      this.getProductResults(this.choices)
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      saveChoices: 'saveChoices',
    }),

    /**
     * Format filter tree param for API call.
     * @returns {String}
     */
    getFilters() {
      if (this.buildFilterTree === true) {
        return '&build_filter_tree=true'
      }

      return ''
    },

    /**
     * Check collection ID before making API call.
     * @returns {String}
     */
    getCollectionScope() {
      let collectionScope = `&collection_scope=${this.collectionId}`

      if (this.collectionId === null) {
        collectionScope = ''
      }

      return collectionScope
    },

    /**
     * Check products per page limit before making API call.
     * @returns {String}
     */
    getProductsPerPage() {
      let limit = `&limit=${this.limit}`

      if (isNaN(this.limit)) {
        limit = ''
      }

      return limit
    },

    /**
     * Format event type so 'init' will be returned on first load.
     * @returns {String}
     */
    formatEventType() {
      if (this.eventType !== 'init') {
        return `&event_type=${this.eventType}`
      }

      if (this.saveEventType === '') {
        this.saveEventType = this.eventType

        return `&event_type=${this.eventType}`
      }

      return '&event_type=filter'
    },

    /**
     * Add unique session ID to API call.
     * @returns {String}
     */
    formatSessionId() {
      const sessionId = JSON.parse(window.localStorage.getItem('boostPFSSessionId'))

      return `&sid=${sessionId}`
    },

    /**
     * Get and format search query for search/suggest endpoint.
     * @returns {String}
     */
    getQuery() {
      let query = ''

      if (this.endpoint === 'search' || this.endpoint === 'search/suggest') {
        query = `&q=${this.query}&fuzzy=2`
      }

      return query
    },

    /**
     * Handle API call to Boost.
     * @param {String} choices - Search parameters to add to request url.
     */
    async getProductResults(choices) {
      try {
        const buildFilterTree = this.getFilters()
        const collectionScope = this.getCollectionScope()
        const formattedEventType = this.formatEventType()
        const limit = this.getProductsPerPage()
        const query = this.getQuery()
        const sessionId = this.formatSessionId()
        const timeNow = `&t=${Date.now()}`
        const countryCode = window.Shopify?.country.toLowerCase() || ''
        const currencyCode = window.Shopify?.currency?.active.toLowerCase() || ''
        let formattedChoices = choices
        let analyticsCollectionScope = collectionScope
        let analyticsQuery = ''
        let analyticsChoices = ''
        this.formattedQuery = ''

        if (this.endpoint === 'search/suggest' && this.query === '') {
          return
        }

        if (choices) {
          analyticsChoices = choices.split('%5B%5D').join('')

        } else {
          formattedChoices = ''
        }

        if (query === '') {
          analyticsCollectionScope = collectionScope.replace('&', '')

        } else {
          this.formattedQuery = `q=${query}&`
          analyticsQuery = `q=${query}`
        }

        this.saveChoices(`${analyticsQuery}${analyticsCollectionScope}${analyticsChoices}`)

        let url = `https://services.mybcapps.com/bc-sf-filter/${this.endpoint}?shop=${this.shop}&country=${countryCode}&currency=${currencyCode}&return_all_currency_fields=true`

        url += buildFilterTree
        url += collectionScope
        url += limit
        url += query
        url += formattedChoices
        url += timeNow
        url += formattedEventType
        url += sessionId

        const response = await fetch(url)
        const data = await response.json()
        this.$emit('boost-updated', data)

      } catch (error) {
        throw new Error(error)
      }
    },
  },
}
</script>
